import React, {useEffect, useRef, useState} from 'react';
import {Button, Checkbox, DatePicker, Form, Modal, notification, Slider, TimePicker} from 'antd';
import classes from './MeetingForm.module.scss';
import Input from "@/app/components/form-fields/input/Input";
import Calendar from "@/app/assets/images/icons/modal/calendar.svg";
import Profile from "@/app/assets/images/icons/modal/user-profile.svg";
import Menu from "@/app/assets/images/icons/modal/menu.svg";
import Card from "@/app/assets/images/icons/modal/card.svg";
import Map from "@/app/assets/images/icons/modal/map.svg";
import Percentage from "@/app/assets/images/icons/modal/percentage.svg";
import Image from 'next/image'
import {useTranslation} from "react-i18next";
import Select from "@/app/components/form-fields/select/Select";
import DisputePopup from "../components/dispute-popup/DisputePopup";
import {addMeeting, fetchContacts, fetchSeekers, verifyMeeting} from "@/app/actions/calendarActions";
import {debounce} from "lodash";
import moment from "moment";
import {useAppContext} from "@/app/context/UserContext";
import RoundedButton from "@/app/components/buttons/rounded-button/RoundedButton";
import useModal from "@/app/hooks/useModal";
import RatePopup from "@/app/[locale]/(main)/calendar/components/rate-popup/RatePopup";
import {cancelMeeting} from "@/app/actions/paymentActions";
import MeetPlatformSelector from "@/app/components/meetPlatformSelector/MeetPlatformSelector";

const MeetingForm = React.memo(({
                                    openNotification,
                                    setUnreadNotifications,
                                    closeFirstModal,
                                    setMeetings,
                                    meetings,
                                    selectedEvent,
                                    setSelectedEvent,
                                    verification = false
                                }) => {

    const [form] = Form.useForm();
    const {ModalComponent, openModal, closeModal} = useModal();
    const [loading, setLoading] = useState(false);
    const [isMeetingFormActive, setIsMeetingFormActive] = useState(true);
    const [seekers, setSeekers] = useState([]);
    const [isRate, setIsRate] = useState();
    const [contacts, setContacts] = useState([]);
    const [seekerQuery, setSeekerQuery] = useState("");
    const [contactQuery, setContactQuery] = useState("");
    const {isMediator,user} = useAppContext();
    const {t} = useTranslation();
    const {RangePicker} = DatePicker;
    const [dates, setDates] = useState([]);
    const [initialTimeRange, setInitialTimeRange] = useState([]);
    const [initialHourMinRange,setInitialHourMinRange] = useState([])
    const [isFreeMeeting,setIsFreeMeeting] = useState(false);
    const eventEndDate = moment(new Date(selectedEvent?.end));
    const today = moment(new Date());
    const differenceInDays = eventEndDate.diff(today, 'days');
    const meetingPassed = moment().isAfter(moment(selectedEvent?.end, "YYYY-MM-DD HH:mm:ss"));

    useEffect(() => {
        if (!verification) {
            fetchSeekers(seekerQuery)
                .then(data => {
                    const options = data.map(({name, email, id}) => ({
                        value: `${id}`, label: `${name} (${email})`,
                    }));
                    setSeekers(options);
                })
        }
    }, [seekerQuery]);

    useEffect(() => {
        if (!verification) {
            fetchContacts(contactQuery)
                .then(data => {
                    const options = data.map(({name, email, id}) => ({
                        value: `${id}`, label: `${name} (${email})`,
                    }));
                    setContacts(options);
                })
        }
    }, [contactQuery]);

    useEffect(() => {
        if (isFreeMeeting) {
            form.setFields([{
                name: 'price', errors: [],
            },]);
        }
    }, [isFreeMeeting])

    const handleFormSubmit = async (data) => {
        try {
            setLoading(true);
            const formData = new FormData();
            for (const key in data) {
                if (Object.hasOwnProperty.call(data, key)) {
                    if ((key === 'address' || key === 'description') && (data[key] === '' || data[key] === undefined)) {
                        continue;
                    }else {
                        formData.append(key, data[key]);
                    }
                }
            }
            if (isFreeMeeting) {
                formData.append('mediator_fee', 0);
            }

            const res = await addMeeting(formData);
            if (res.data) {
                const newMeeting = {
                  ...res,
                    start: moment(res.data.start_time).toDate(),
                    end: moment(res.data.end_time).toDate(),
                    guest: res.data.guest.name + `(${res.data.guest.email})`,
                    seeker: res.data.seeker.name + `(${res.data.seeker.email})`
                };
                setMeetings([...meetings, newMeeting]);
                openNotification('success', 'bottomRight', t('notifications.successfullyAddedMeet'));
                closeFirstModal();
            } else {
                if(res.message) {
                    openNotification('error', 'bottomRight', res?.message);
                }else {
                    openNotification('error', 'bottomRight', t('notifications.error'));
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (values) => {
        const {timeRange, timeRangee, ...rest} = values;
        const startDate = timeRange[0].format('YYYY-MM-DD');
        const endDate = timeRange[1].format('YYYY-MM-DD');
        const startTime = timeRangee[0].format('HH:mm');
        const endTime = timeRangee[1].format('HH:mm');

        const startDateTime = `${startDate} ${startTime}`;
        const endDateTime = `${endDate} ${endTime}`;
        // const startTime = timeRange[0].format('YYYY-MM-DD HH:mm');
        // const endTime = timeRange[1].format('YYYY-MM-DD HH:mm');
        const formattedValues = {
            ...rest,
            'timeRange[0]': startDateTime,
            'timeRange[1]': endDateTime,
            // timeRange: [startTime, endTime],
        };
        handleFormSubmit(formattedValues);
    };

    const handleCancel = (isDeletingMeeting = false) => {
        Modal.confirm({
            title: null,

            content: (<div>
                <h3>{isDeletingMeeting ? t('modal.deleteMeeting') : t('modal.cancelSchedulingConfirmation')}</h3>
            </div>),
            onOk: async () => {
                if(isDeletingMeeting) {
                    if(selectedEvent.status === 'SCHEDULED' || selectedEvent.status === 'PAID') {
                        await cancelMeeting(selectedEvent.id).then((data) => {
                            if (data.status >= 200 && data.status <= 299) {
                                openNotification('success', 'bottomRight', t('notifications.successfullyCanceledMeeting'));
                                closeModal();
                                closeFirstModal();
                                setIsMeetingFormActive(false);
                            } else if (data.status === 403) {
                                if(data.data.message) {
                                    openNotification('error', 'bottomRight', t('notifications.meetingAlreadyCancelled'));
                                }else {
                                    openNotification('error', 'bottomRight', t('notifications.notOwner'));
                                }
                            }else {
                                openNotification('error', 'bottomRight', t('notifications.serverError'));
                            }
                        })
                    }else {
                        if(selectedEvent.status === 'CANCELLED') {
                            openNotification('error', 'bottomRight', t('notifications.meetingAlreadyCancelled'));
                        }else {
                            openNotification('error', 'bottomRight', t('notifications.meetingStatus'));
                        }
                    }
                }else {
                    closeFirstModal();
                    closeModal();
                    setIsMeetingFormActive(false);
                }
                form.resetFields();
            },
            centered: true,
            className: classes.confirmationModal,
            icon: null,
            okText: isDeletingMeeting ? t('modal.yes') : t('modal.cancel'),
            cancelText: isDeletingMeeting ? t('modal.no') : t('modal.back')
        });
    };
    const formatter = (value) => {
        return `${value}%`;
    };
    const disabledDate = current => {
        return current && current < moment().startOf('day');
    };

    const disabledTime = (current, type) => {
        if (!current || !moment(current).isValid()) return {};
        const now = moment();
        const startMonth = dates[0]?.format('MMMM')
        const startDay = dates[0]?.format('D');
        const endDay = dates[1]?.format('D');
        const todayMonth = now.format('MMMM');
        const todayDay = now.format('D');
        const startTime = initialTimeRange[0];
        const endTime = initialTimeRange[1];
        if (type === 'start' && startMonth) {
            if (startMonth === todayMonth && startDay === todayDay) {
                return {
                    disabledHours: () => Array.from({length: now.hour()}, (_, i) => i),
                    disabledMinutes: (selectedHour) => selectedHour === now.hour() ? Array.from({length: now.minute() + 15}, (_, i) => i) : [],
                };
            }
        }
        if (type === 'end' && startDay === endDay) {
            const selectedHour = current.hour();
            const selectedMinute = current.minute();

            // Disable the exact same time as the start time (e.g., if start is 13:15, disable 13:15)
            if (startTime?.hour() === selectedHour && startTime?.minute() === selectedMinute) {
                return {
                    disabledHours: () => [], disabledMinutes: () => [selectedMinute], // Disable the same minute only
                };
            }
        }
        return {};
    };
    const handleCalendarChange = (dates) => {
        setDates(dates);
        form.setFieldValue('timeRangee', []);
    };

    useEffect(() => {
        if (selectedEvent) {
            const start = moment(selectedEvent.start);
            const end = moment(selectedEvent.end);
            setInitialTimeRange([start, end]);

            const startTime = moment(start.format('hh:mm A'), 'hh:mm A');
            const endTime = moment(end.format('hh:mm A'), 'hh:mm A');
            setInitialHourMinRange([startTime,endTime])
            form.setFieldsValue({
                timeRange: [start, end],
                timeRangee: [startTime, endTime],
            });
        }
    }, [selectedEvent]);

    const handleFreeMeetingChange = (e) => {
        setIsFreeMeeting(e.target.checked)
        form.setFieldsValue({ price: e.target.checked ? 0 : undefined });
    };
    const [api, contextHolder] = notification.useNotification();


    const meetingVerification = () => {
        const openNotification2 = (type, placement, text) => {
            api[type]({
                message: text, description: text, placement,
            });
        };
        verifyMeeting(selectedEvent.meeting_id).then((res) => {
            if (res >= 200 && res <= 299) {
                setLoading(true)

                openNotification2('success', 'bottomRight', t('notifications.successfullyVerifiedMeet'));

                setTimeout(() => {
                    closeFirstModal();
                    // setLoading(false);
                }, 1000);
            } else {
                openNotification2('error', 'bottomRight', t('notifications.serverError'))
                setLoading(false);
            }
        })
    }

    return (<>
        {contextHolder}
        {isMeetingFormActive && (<Form
            form={form}
            className={`${classes.meetingForm} ${selectedEvent ? classes.disabledForm : ''}`}
            onFinish={handleSubmit}
            initialValues={selectedEvent ? {
                title: selectedEvent.title,
                timeRange: initialTimeRange,
                timeRangee: initialHourMinRange,
                seeker: selectedEvent.seeker,
                guest: selectedEvent.guest,
                address: selectedEvent.address,
                description: selectedEvent.description,
                price: selectedEvent.price,
                mediator_fee: selectedEvent.mediator_fee
            } : {}}
            disabled={selectedEvent}
        >
            <div className={`${classes.info} ${classes.header}`}>
                <Image src={Calendar} alt='Calendar'/>
                <Form.Item
                    name="title"
                    rules={[{required: true}]}
                >
                    <Input
                        placeholder={t('placeholders.meetingName')}/>
                </Form.Item>
            </div>
            <div className={classes.rangeDate}>
                <Form.Item
                    className={classes.customRange}
                    name="timeRange"
                    rules={[{required: true, message: t('validation.dateRange')}]}
                >
                    <RangePicker
                        showTime
                        suffixIcon={null}
                        format="YYYY-MM-DD"
                        disabledDate={disabledDate}
                        onCalendarChange={handleCalendarChange}
                    />
                </Form.Item>
                <Form.Item
                    className={classes.customRange}
                    name="timeRangee"
                    rules={[{required: true,message: t('validation.timeRange')}]}
                >
                    {/*defaultValue={[startTime, endTime]}*/}
                    <TimePicker.RangePicker  suffixIcon={null} format={'hh:mm A'} />
                </Form.Item>
            </div>
            <div className={classes.info}>
                <Image src={Profile} alt='Profile'/>
                <Form.Item
                    name="seeker"
                    rules={[{required: true}]}
                >
                    <Select
                        className={classes.customSelect}
                        placeholder={t('placeholders.addSeeker')}
                        filterOption={() => true}
                        onSearch={debounce((e) => setSeekerQuery(e), 500)}
                        options={seekers}
                    />
                </Form.Item>
            </div>

            <div className={classes.info}>
                <Image src={Profile} alt='Profile'/>
                <Form.Item
                    name="guest"
                    rules={[{
                        required: true, message: t('validation.contactRequired'),
                    }]}
                >
                    <Select
                        className={classes.customSelect}
                        placeholder={t('placeholders.addContact')}
                        filterOption={() => true}
                        onSearch={debounce((e) => setContactQuery(e), 500)}
                        options={contacts}
                    />
                </Form.Item>
            </div>

            <div className={classes.info}>
                <Image src={Map} alt='Map'/>
                <Form.Item
                    name="address"
                    rules={[{
                        min: 2, max: 100, whitespace: true,
                    },]}
                >
                    <Input placeholder={t('placeholders.address')}/>
                </Form.Item>
            </div>
            <div className={classes.info}>
                <Image src={Menu} alt='Menu'/>
                <Form.Item
                    name="description"
                    rules={[{
                        min: 2, max: 100, whitespace: true,
                    },]}
                >
                    <Input placeholder={t('placeholders.description')}/>
                </Form.Item>
            </div>
            <div className={`${classes.info} ${classes.price}`}>
                <Image src={Card} alt='Price'/>
                <Form.Item
                    name="price"
                    initialValue={isFreeMeeting ? 0 : undefined}
                    rules={isFreeMeeting ? [] : [
                        { required: true },
                        {
                            validator: (_, value) => {
                                if (isNaN(value)) {
                                    return Promise.reject(t('validation.numberValid'));
                                } else if (value <= 0) {
                                    return Promise.reject(t('validation.numberGreaterThanZero'));
                                } else if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                                    return Promise.reject(t('validation.maxTwoDecimals'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    // rules={!isFreeMeeting && [{required: true}, {
                    //         validator: (_, value) => {
                    //                 if (isNaN(value)) {
                    //                     return Promise.reject(t('validation.numberValid'));
                    //                 } else if (value <= 0) {
                    //                     return Promise.reject(t('validation.numberGreaterThanZero'));
                    //                 } else if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                    //                     return Promise.reject(t('validation.maxTwoDecimals'));
                    //                 }
                    //                 return Promise.resolve();
                    //         }
                    // }]}
                >
                    <Input className={classes.priceInput} disabled={isFreeMeeting} placeholder={t('placeholders.cost')}/>
                </Form.Item>{!isFreeMeeting && <p style={{fontSize: '16px'}}>€</p>}
            </div>
            {!verification && <Form.Item
                name="provider"
                rules={[{ required: true, message: t('validation.selectMeetLocation') }]}
            >
                <MeetPlatformSelector
                    value={form.getFieldValue('provider')}
                    onChange={(value) => form.setFieldsValue({ provider: value })}
                />
            </Form.Item>}

            <Checkbox className={classes.checkbox}
                      onChange={(e) => handleFreeMeetingChange(e)}
            ><p className={classes.checkbox_text}>{t('payment.freeMeeting')}</p>
            </Checkbox>
            {isMediator && !isFreeMeeting && (<div className={classes.info}>
                <Image src={Percentage} alt='Percentage'/>
                <Form.Item
                    name="mediator_fee"
                    initialValue={50}
                >
                    <Slider
                        className={classes.slider}
                        marks={{0: 'CONNECTOR', 100: 'CONTACT'}}
                        defaultValue={50}
                        tipFormatter={formatter}
                    />
                </Form.Item>
            </div>)}
            {selectedEvent && verification ? (<div className={classes.verification}>
                <p>{t('modal.verify')}
                    {moment(new Date(selectedEvent.start)).format('dddd, MMMM D, h:mm a')} -
                    &nbsp;{moment(new Date(selectedEvent.end)).format('h:mm a')}
                </p>
                <div>
                    {differenceInDays > -7 && <span onClick={() => {
                        openModal();
                        setIsRate(false);
                    }} className={classes.dispute}>{t('buttons.openDispute')}</span>}
                    <RoundedButton onClick={() => {
                        if (selectedEvent.mediator.name === "Deleted User" && selectedEvent.mediator.average_rating === '0.00') {
                            setIsRate(false);
                            meetingVerification();
                        } else {
                            setIsRate(true);
                            openModal();
                        }
                    }} disabled={loading} type="primary" htmlType="button">
                        {t('buttons.verify')}
                    </RoundedButton>
                </div>
            </div>) : selectedEvent ? <div className={classes.cancelMeetingBtnWrapper}>
                {!meetingPassed && isMediator && <RoundedButton disabled={false} onClick={() => {
                    handleCancel(true);
                }}>
                    {t('modal.cancelMeeting')}
                </RoundedButton>}</div> : (<div className={classes.buttonsWrapper}>
                <p onClick={() => handleCancel(!!selectedEvent)} style={{marginLeft: '8px'}}>
                    {t('modal.cancel')}
                </p>
                <Button loading={loading} type="primary" htmlType="submit">
                    {t('modal.save')}
                </Button>
            </div>)}
            <ModalComponent closeIcon={null}>
                {!isRate ? <DisputePopup setUnreadNotifications={setUnreadNotifications} data={selectedEvent}
                                         closeFirstModal={closeFirstModal} closeModal={closeModal}/> :
                    <RatePopup setUnreadNotifications={setUnreadNotifications} data={selectedEvent}
                               closeFirstModal={closeFirstModal} closeModal={closeModal}/>}
            </ModalComponent>
        </Form>)}
    </>);
});

export default MeetingForm;



